import Section from '@lyra/core/components/Section'
import filterNulls from '@lyra/core/utils/filterNulls'
import TabsHorizontal from '@lyra/web/components/layout/TabsHorizontal'
import { InstrumentType, SelectedInstrument } from '@lyra/web/constants/instruments'
import {
  OrderQuoteParams,
  OrderType,
  SetOrderQuoteParams,
  TriggerType,
} from '@lyra/web/constants/order'
import { SendRfqParams, SetRfqParams } from '@lyra/web/constants/rfqs'
import { parseInstrumentName } from '@lyra/web/utils/instruments'
import { getDefaultOrderQuoteParams } from '@lyra/web/utils/order'
import { getRfqParamsFromOrderParams } from '@lyra/web/utils/rfqs'
import { useCallback, useMemo } from 'react'

type Props = {
  instruments: SelectedInstrument[]
  orderParams: OrderQuoteParams | null
  setOrderParams: SetOrderQuoteParams
  // rfq disabled if rfq props not provided
  isRfqDisabled?: boolean
  isRfq?: boolean
  rfqParams?: SendRfqParams
  setRfqParams?: SetRfqParams
  disableTriggers?: boolean
}

enum OrderTypeTab {
  Limit = 'Limit',
  Market = 'Market',
  Rfq = 'Rfq',
  TakeProfitLimit = 'TakeProfitLimit',
  TakeProfitMarket = 'TakeProfitMarket',
  StopLimit = 'StopLimit',
  StopMarket = 'StopMarket',
}

export default function TradeFormOrderTypeTabs({
  isRfq,
  instruments,
  rfqParams,
  isRfqDisabled,
  setRfqParams,
  orderParams,
  setOrderParams,
  disableTriggers,
}: Props) {
  const selectedTab = isRfq
    ? OrderTypeTab.Rfq
    : !!orderParams && orderParams.order_type === OrderType.Market && !orderParams.trigger_type
    ? OrderTypeTab.Market
    : !!orderParams && orderParams.trigger_type === TriggerType.TakeProfit
    ? orderParams.order_type === OrderType.Limit
      ? OrderTypeTab.TakeProfitLimit
      : OrderTypeTab.TakeProfitMarket
    : !!orderParams && orderParams.trigger_type === TriggerType.StopLoss
    ? orderParams.order_type === OrderType.Limit
      ? OrderTypeTab.StopLimit
      : OrderTypeTab.StopMarket
    : OrderTypeTab.Limit

  const handleSetTab = useCallback(
    (tab: OrderTypeTab) => {
      if (selectedTab === tab) {
        return
      }

      if (tab === OrderTypeTab.Rfq) {
        if (!orderParams) {
          console.warn('no orderParmas to switch to rfq')
          return
        }

        if (!setRfqParams) {
          console.warn('rfq is not enabled')
          return
        }

        setRfqParams(getRfqParamsFromOrderParams(orderParams))
      } else {
        if (!instruments.length) {
          return
        }

        const newOrderParams = getDefaultOrderQuoteParams(instruments[0].instrument_name)
        if (rfqParams?.legs.length) {
          // copy first rfq leg amount+direction over
          newOrderParams.direction = rfqParams.legs[0].direction
          newOrderParams.amount = rfqParams.legs[0].amount
        } else if (orderParams) {
          // copy direction / amount from previous order type
          newOrderParams.direction = orderParams.direction
          newOrderParams.amount = orderParams.amount
        }

        if (tab === OrderTypeTab.Market) {
          setOrderParams({
            ...newOrderParams,
            order_type: OrderType.Market,
            trigger_type: undefined,
          })
        } else if (tab === OrderTypeTab.StopLimit) {
          setOrderParams({
            ...newOrderParams,
            order_type: OrderType.Limit,
            trigger_type: TriggerType.StopLoss,
          })
        } else if (tab === OrderTypeTab.StopMarket) {
          setOrderParams({
            ...newOrderParams,
            order_type: OrderType.Market,
            trigger_type: TriggerType.StopLoss,
          })
        } else if (tab === OrderTypeTab.TakeProfitLimit) {
          setOrderParams({
            ...newOrderParams,
            order_type: OrderType.Limit,
            trigger_type: TriggerType.TakeProfit,
          })
        } else if (tab === OrderTypeTab.TakeProfitMarket) {
          setOrderParams({
            ...newOrderParams,
            order_type: OrderType.Market,
            trigger_type: TriggerType.TakeProfit,
          })
        } else {
          setOrderParams({
            ...newOrderParams,
            order_type: OrderType.Limit,
            trigger_type: undefined,
          })
        }
      }
    },
    [instruments, selectedTab, orderParams, setOrderParams, rfqParams, setRfqParams]
  )

  // all legs are options
  const isRfqEnabled =
    useMemo(() => {
      return instruments.every(
        ({ instrument_name }) =>
          parseInstrumentName(instrument_name)?.type === InstrumentType.Options
      )
    }, [instruments]) && !isRfqDisabled

  const instrumentType = parseInstrumentName(instruments[0].instrument_name)?.type
  const isMarketOrderEnabled =
    instruments.length === 1 &&
    (instrumentType === InstrumentType.Perps || instrumentType === InstrumentType.Spot)
  const isTriggersEnabled = instrumentType !== InstrumentType.Spot && !disableTriggers

  return (
    <Section.XStack flexShrink={1} isCompact>
      <TabsHorizontal
        pages={filterNulls([
          {
            name: 'Limit',
            onPress: () => handleSetTab(OrderTypeTab.Limit),
            isSelected: selectedTab === OrderTypeTab.Limit,
          },
          isMarketOrderEnabled
            ? {
                name: 'Market',
                onPress: () => handleSetTab(OrderTypeTab.Market),
                isSelected: selectedTab === OrderTypeTab.Market,
              }
            : isRfqEnabled
            ? {
                name: 'RFQ',
                onPress: () => handleSetTab(OrderTypeTab.Rfq),
                isSelected: selectedTab === OrderTypeTab.Rfq,
              }
            : null,
          isTriggersEnabled
            ? {
                name:
                  selectedTab === OrderTypeTab.TakeProfitLimit ||
                  selectedTab === OrderTypeTab.TakeProfitMarket
                    ? 'TP'
                    : selectedTab === OrderTypeTab.StopLimit ||
                      selectedTab === OrderTypeTab.StopMarket
                    ? 'SL'
                    : 'More',
                isSelected:
                  selectedTab === OrderTypeTab.TakeProfitLimit ||
                  selectedTab === OrderTypeTab.TakeProfitMarket ||
                  selectedTab === OrderTypeTab.StopLimit ||
                  selectedTab === OrderTypeTab.StopMarket,
                dropdown: filterNulls([
                  {
                    name: 'Take Profit Limit',
                    onPress: () => handleSetTab(OrderTypeTab.TakeProfitLimit),
                    isSelected: selectedTab === OrderTypeTab.TakeProfitLimit,
                  },
                  isMarketOrderEnabled
                    ? {
                        name: 'Take Profit Market',
                        onPress: () => handleSetTab(OrderTypeTab.TakeProfitMarket),
                        isSelected: selectedTab === OrderTypeTab.TakeProfitMarket,
                      }
                    : null,
                  {
                    name: 'Stop Limit',
                    onPress: () => handleSetTab(OrderTypeTab.StopLimit),
                    isSelected: selectedTab === OrderTypeTab.StopLimit,
                  },
                  isMarketOrderEnabled
                    ? {
                        name: 'Stop Market',
                        onPress: () => handleSetTab(OrderTypeTab.StopMarket),
                        isSelected: selectedTab === OrderTypeTab.StopMarket,
                      }
                    : null,
                ]),
              }
            : null,
        ])}
        width="100%"
        borderBottomColor="$hairline"
        borderBottomWidth={1}
        size="sm"
        isBodyText
        tabProps={{
          flexGrow: 1,
          flexShrink: 1,
          flexBasis: 0,
        }}
      />
    </Section.XStack>
  )
}
